import React, { useState } from 'react';
import {
  Sparkles,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Building2,
  Ship,
  Layers,
  Palette,
  Compass,
  FileText,
  ShieldAlert,
  Loader2,
  Globe,
  Sliders,
  Check,
  RefreshCw,
  ExternalLink,
  Zap,
} from 'lucide-react';
import {
  IndustryType,
  AIDesignVibe,
  AIBusinessProfile,
  AISitePlan,
  AIJobProgress,
} from '../../types';
import { aiClientService } from '../../services/aiService';
import { useSite } from '../../context/SiteContext';
import confetti from 'canvas-confetti';

interface AISiteGeneratorWizardProps {
  isOpen?: boolean;
  initialIndustry?: IndustryType;
  onClose: () => void;
  onSuccess?: (siteId: string) => void;
  onSiteCreated?: (siteId: string) => void;
}

type WizardStep = 'PROMPT_INPUT' | 'DESIGN_VIBE' | 'PAGES_MODULES' | 'GENERATING' | 'REVIEW_DRAFT';

const QUICK_PROMPTS = [
  {
    title: 'Muğla / Göcek Lüks Yat Kiralama',
    industry: 'YACHT_RENTAL' as IndustryType,
    vibe: 'AEGEAN_COASTAL' as AIDesignVibe,
    companyName: 'Azure Aegean Yachting',
    location: 'Göcek / Muğla',
    prompt: 'Muğla Göcek ve Dalaman koylarında 8 adet lüks yat ve gulet ile VIP mavi tur hizmeti veren premium charter acentesi.',
  },
  {
    title: 'Kurumsal Danışmanlık & Teknoloji Holdingi',
    industry: 'CORPORATE' as IndustryType,
    vibe: 'CORPORATE_PRESTIGE' as AIDesignVibe,
    companyName: 'Vanguard Global Holding',
    location: 'İstanbul / Maslak',
    prompt: 'B2B yönetim danışmanlığı, finansal denetim ve kurumsal dijital dönüşüm çözümleri sunan uluslararası danışmanlık firması.',
  },
  {
    title: 'Bodrum VIP Katamaran & Gulet Charter',
    industry: 'YACHT_RENTAL' as IndustryType,
    vibe: 'LUXURY_DARK' as AIDesignVibe,
    companyName: 'Bodrum Royal Charters',
    location: 'Bodrum / Muğla',
    prompt: 'Bodrum ve Yunan Adaları rotalarında özel şefli, jakuzili lüks katamaran ve mega yat kiralama servisi.',
  },
  {
    title: 'Yenilikçi Fintek & Yazılım Çözümleri',
    industry: 'CORPORATE' as IndustryType,
    vibe: 'TECH_VIBRANT' as AIDesignVibe,
    companyName: 'NovaCore Technologies',
    location: 'Ankara / ODTÜ Teknokent',
    prompt: 'Bulut tabanlı kurumsal finans, ERP entegrasyonları ve yapay zeka destekli veri analitiği yazılımları geliştiren teknoloji şirketi.',
  },
];

const DESIGN_VIBES: {
  id: AIDesignVibe;
  title: string;
  desc: string;
  colors: string[];
  bgPreview: string;
}[] = [
  {
    id: 'AEGEAN_COASTAL',
    title: 'Ege & Akdeniz Coastal',
    desc: 'Turkuaz deniz mavisi, sıcak altın tonları ve ferah sahil atmosferi.',
    colors: ['#0369A1', '#0C4A6E', '#F59E0B', '#F8FAFC'],
    bgPreview: 'from-sky-950 via-slate-900 to-sky-900',
  },
  {
    id: 'LUXURY_DARK',
    title: 'Midnight Luxury Dark',
    desc: 'Zarif koyu arkaplan, altın sarısı aksanlar ve VIP lüks prestij hissi.',
    colors: ['#0F172A', '#D97706', '#1E293B', '#F8FAFC'],
    bgPreview: 'from-slate-950 via-gray-900 to-black',
  },
  {
    id: 'CORPORATE_PRESTIGE',
    title: 'Kurumsal Prestij',
    desc: 'Derin lacivert, güven veren gri tonları ve net iş dünyası estetiği.',
    colors: ['#1E40AF', '#0F172A', '#2563EB', '#FFFFFF'],
    bgPreview: 'from-blue-950 via-slate-900 to-slate-950',
  },
  {
    id: 'MODERN_MINIMAL',
    title: 'Modern & Minimalist',
    desc: 'Yüksek kontrast, geniş beyaz boşluklar ve rafine İsviçre tipografisi.',
    colors: ['#18181B', '#3F3F46', '#0284C7', '#FAFAFA'],
    bgPreview: 'from-zinc-900 via-neutral-900 to-zinc-950',
  },
  {
    id: 'TECH_VIBRANT',
    title: 'Tech & Dynamic SaaS',
    desc: 'Canlı mor ve zümrüt yeşili aksanlar ile modern teknoloji ve girişim vizyonu.',
    colors: ['#4F46E5', '#10B981', '#064E3B', '#090D16'],
    bgPreview: 'from-indigo-950 via-slate-900 to-emerald-950',
  },
];

export const AISiteGeneratorWizard: React.FC<AISiteGeneratorWizardProps> = ({
  isOpen = true,
  initialIndustry,
  onClose,
  onSuccess,
  onSiteCreated,
}) => {
  const { createSiteWithAI, sectorQuotas, canCreateSectorWebsite } = useSite();

  const [step, setStep] = useState<WizardStep>('PROMPT_INPUT');
  const [industry, setIndustry] = useState<IndustryType>(initialIndustry || 'CORPORATE');
  const [companyName, setCompanyName] = useState(
    initialIndustry === 'YACHT_RENTAL' ? 'Azure Aegean Yachting' : 'Vanguard Global Danışmanlık'
  );
  const [promptText, setPromptText] = useState(
    initialIndustry === 'YACHT_RENTAL'
      ? 'Muğla Dalaman ve Göcek koylarında 8 adet lüks yat ile VIP kiralama ve mavi tur hizmeti veren acenteyiz. Misafirlerimiz WhatsApp veya rezervasyon formundan ulaşabilir.'
      : 'İstanbul Maslak merkezli; yönetim danışmanlığı, kurumsal finansal denetim ve dijital dönüşüm hizmetleri sunan uluslararası B2B danışmanlık firması.'
  );
  const [location, setLocation] = useState(initialIndustry === 'YACHT_RENTAL' ? 'Göcek / Muğla' : 'İstanbul / Maslak');
  const [phone, setPhone] = useState(initialIndustry === 'YACHT_RENTAL' ? '+90 (252) 614 88 00' : '+90 (212) 280 00 00');
  const [email, setEmail] = useState(initialIndustry === 'YACHT_RENTAL' ? 'info@azureaegean.com' : 'contact@vanguardglobal.com');
  const [whatsapp, setWhatsapp] = useState('+90 (532) 123 45 67');
  const [toneOfVoice, setToneOfVoice] = useState<'PREMIUM_LUXURY' | 'PROFESSIONAL_CORPORATE' | 'WARM_FRIENDLY' | 'DYNAMIC_INNOVATIVE'>(
    initialIndustry === 'YACHT_RENTAL' ? 'PREMIUM_LUXURY' : 'PROFESSIONAL_CORPORATE'
  );
  const [selectedVibe, setSelectedVibe] = useState<AIDesignVibe>(
    initialIndustry === 'YACHT_RENTAL' ? 'AEGEAN_COASTAL' : 'CORPORATE_PRESTIGE'
  );

  React.useEffect(() => {
    if (initialIndustry === 'CORPORATE') {
      setIndustry('CORPORATE');
      setCompanyName('Vanguard Global Danışmanlık');
      setPromptText('İstanbul Maslak merkezli; yönetim danışmanlığı, kurumsal finansal denetim ve dijital dönüşüm hizmetleri sunan uluslararası B2B danışmanlık firması.');
      setLocation('İstanbul / Maslak');
      setPhone('+90 (212) 280 00 00');
      setEmail('contact@vanguardglobal.com');
      setToneOfVoice('PROFESSIONAL_CORPORATE');
      setSelectedVibe('CORPORATE_PRESTIGE');
    } else if (initialIndustry === 'YACHT_RENTAL') {
      setIndustry('YACHT_RENTAL');
      setCompanyName('Azure Aegean Yachting');
      setPromptText('Muğla Dalaman ve Göcek koylarında 8 adet lüks yat ile VIP kiralama ve mavi tur hizmeti veren acenteyiz. Misafirlerimiz WhatsApp veya rezervasyon formundan ulaşabilir.');
      setLocation('Göcek / Muğla');
      setPhone('+90 (252) 614 88 00');
      setEmail('info@azureaegean.com');
      setToneOfVoice('PREMIUM_LUXURY');
      setSelectedVibe('AEGEAN_COASTAL');
    }
  }, [initialIndustry]);

  const [selectedPages, setSelectedPages] = useState<string[]>([
    'home',
    'about',
    'fleet_or_services',
    'contact',
    'blog',
  ]);

  // Generation state
  const [jobProgress, setJobProgress] = useState<AIJobProgress>({
    stage: 'ANALYSIS',
    progressPercent: 0,
    message: 'Hazırlanıyor...',
  });
  const [generatedPlan, setGeneratedPlan] = useState<AISitePlan | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  if (isOpen === false) {
    return null;
  }

  const applyQuickPrompt = (item: typeof QUICK_PROMPTS[0]) => {
    if (!canCreateSectorWebsite(item.industry)) {
      const quota = sectorQuotas[item.industry];
      setErrorMsg(
        quota.allowed
          ? `${item.industry === 'YACHT_RENTAL' ? 'Yat Kiralama' : 'Kurumsal'} sektöründe site kotanız dolmuştur (${quota.used}/${quota.total}).`
          : `${item.industry === 'YACHT_RENTAL' ? 'Yat Kiralama' : 'Kurumsal'} paketi aboneliğinizde tanımlı değildir.`
      );
      return;
    }
    setErrorMsg(null);
    setIndustry(item.industry);
    setCompanyName(item.companyName);
    setLocation(item.location);
    setPromptText(item.prompt);
    setSelectedVibe(item.vibe);
    if (item.industry === 'YACHT_RENTAL') {
      setToneOfVoice('PREMIUM_LUXURY');
      setPhone('+90 (252) 614 88 00');
      setEmail(`info@${item.companyName.toLowerCase().replace(/[^a-z0-9]/g, '')}.com`);
    } else {
      setToneOfVoice('PROFESSIONAL_CORPORATE');
      setPhone('+90 (212) 280 00 00');
      setEmail(`contact@${item.companyName.toLowerCase().replace(/[^a-z0-9]/g, '')}.com`);
    }
  };

  const handleStartGeneration = async () => {
    setStep('GENERATING');
    setErrorMsg(null);

    const profile: AIBusinessProfile = {
      prompt: promptText,
      companyName,
      industry,
      location,
      phone,
      email,
      whatsapp,
      vibe: selectedVibe,
      toneOfVoice,
      requestedPages: selectedPages,
    };

    try {
      const plan = await aiClientService.generateSitePlan(profile, (prog) => {
        setJobProgress(prog);
      });

      setGeneratedPlan(plan);
      setStep('REVIEW_DRAFT');
      confetti({
        particleCount: 80,
        spread: 70,
        origin: { y: 0.6 },
      });
    } catch (err: any) {
      console.error('Generation failed:', err);
      setErrorMsg(err?.message || 'Yapay zeka site üretiminde beklenmedik bir durum oluştu.');
      setStep('PROMPT_INPUT');
    }
  };

  const handleAcceptAndOpenEditor = async () => {
    if (!generatedPlan) return;
    setIsSaving(true);
    try {
      const newSite = await createSiteWithAI(generatedPlan);
      if (onSiteCreated) {
        onSiteCreated(newSite.id);
      }
      if (onSuccess) {
        onSuccess(newSite.id);
      }
      onClose();
    } catch (err: any) {
      console.error('Error creating site from AI plan:', err);
      setErrorMsg('Site taslağı kaydedilirken hata oluştu: ' + (err?.message || 'Bilinmeyen hata'));
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4 overflow-y-auto">
      <div className="relative w-full max-w-4xl bg-slate-900 border border-slate-700/80 rounded-2xl shadow-2xl overflow-hidden flex flex-col my-8">
        {/* Top Header */}
        <div className="px-6 py-4 border-b border-slate-800 bg-gradient-to-r from-slate-900 via-indigo-950/40 to-slate-900 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-sky-500 flex items-center justify-center text-white shadow-lg shadow-indigo-500/20">
              <Sparkles className="w-5 h-5 animate-pulse" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold text-white tracking-wide">
                  Adiosoft AI Website Generator
                </h2>
                <span className="px-2 py-0.5 text-xs font-semibold uppercase tracking-wider bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 rounded-full">
                  Gemini 3.7 Engine
                </span>
              </div>
              <p className="text-xs text-slate-400">
                Tek bir cümleyle komple kurumsal veya yat kiralama web sitesi mimarisi üretin.
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white p-2 rounded-lg hover:bg-slate-800 transition"
          >
            ✕
          </button>
        </div>

        {/* Step Indicator */}
        {step !== 'GENERATING' && (
          <div className="px-6 py-3 bg-slate-950/60 border-b border-slate-800 flex items-center justify-between text-xs text-slate-400">
            <div className="flex items-center gap-2">
              <span
                className={`w-6 h-6 rounded-full flex items-center justify-center font-bold ${
                  step === 'PROMPT_INPUT'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-slate-800 text-slate-300'
                }`}
              >
                1
              </span>
              <span className={step === 'PROMPT_INPUT' ? 'text-white font-medium' : ''}>
                İşletme & Prompt
              </span>
            </div>
            <div className="h-[1px] w-8 bg-slate-800 hidden sm:block" />

            <div className="flex items-center gap-2">
              <span
                className={`w-6 h-6 rounded-full flex items-center justify-center font-bold ${
                  step === 'DESIGN_VIBE'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-slate-800 text-slate-300'
                }`}
              >
                2
              </span>
              <span className={step === 'DESIGN_VIBE' ? 'text-white font-medium' : ''}>
                Tasarım Yönü
              </span>
            </div>
            <div className="h-[1px] w-8 bg-slate-800 hidden sm:block" />

            <div className="flex items-center gap-2">
              <span
                className={`w-6 h-6 rounded-full flex items-center justify-center font-bold ${
                  step === 'PAGES_MODULES'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-slate-800 text-slate-300'
                }`}
              >
                3
              </span>
              <span className={step === 'PAGES_MODULES' ? 'text-white font-medium' : ''}>
                Sayfalar & Modüller
              </span>
            </div>
            <div className="h-[1px] w-8 bg-slate-800 hidden sm:block" />

            <div className="flex items-center gap-2">
              <span
                className={`w-6 h-6 rounded-full flex items-center justify-center font-bold ${
                  step === 'REVIEW_DRAFT'
                    ? 'bg-emerald-600 text-white'
                    : 'bg-slate-800 text-slate-300'
                }`}
              >
                4
              </span>
              <span className={step === 'REVIEW_DRAFT' ? 'text-emerald-400 font-medium' : ''}>
                Taslak İnceleme
              </span>
            </div>
          </div>
        )}

        {/* Modal Body */}
        <div className="p-6 flex-1 overflow-y-auto max-h-[65vh]">
          {errorMsg && (
            <div className="mb-4 p-4 rounded-xl bg-red-500/10 border border-red-500/30 flex items-start gap-3 text-red-300 text-sm">
              <ShieldAlert className="w-5 h-5 flex-shrink-0 text-red-400 mt-0.5" />
              <div>
                <p className="font-semibold">Bir sorun oluştu</p>
                <p className="text-xs text-red-300/80">{errorMsg}</p>
              </div>
            </div>
          )}

          {/* STEP 1: PROMPT INPUT */}
          {step === 'PROMPT_INPUT' && (
            <div className="space-y-6">
              {/* Quick Template Inspirations */}
              <div>
                <label className="text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2 block flex items-center gap-1.5">
                  <Zap className="w-3.5 h-3.5 text-amber-400" /> Hızlı İlham & Örnek Senaryolar:
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {QUICK_PROMPTS.map((qp, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => applyQuickPrompt(qp)}
                      className="text-left p-3 rounded-xl bg-slate-800/60 hover:bg-slate-800 border border-slate-700/60 hover:border-indigo-500/50 transition group"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-bold text-white group-hover:text-indigo-300 transition flex items-center gap-1.5">
                          {qp.industry === 'YACHT_RENTAL' ? (
                            <Ship className="w-3.5 h-3.5 text-sky-400" />
                          ) : (
                            <Building2 className="w-3.5 h-3.5 text-amber-400" />
                          )}
                          {qp.title}
                        </span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-900 text-slate-400">
                          {qp.industry === 'YACHT_RENTAL' ? 'Yat Kiralama' : 'Kurumsal'}
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-400 line-clamp-2">{qp.prompt}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Sektör Seçimi */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-slate-300 uppercase tracking-wider block">
                    1. Sektör Seçin:
                  </label>
                  <span className="text-[11px] text-slate-400">
                    Paket Kotaları: Kurumsal ({sectorQuotas.CORPORATE.used}/{sectorQuotas.CORPORATE.total}) • Yat ({sectorQuotas.YACHT_RENTAL.used}/{sectorQuotas.YACHT_RENTAL.total})
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Yat Sektörü */}
                  <button
                    type="button"
                    onClick={() => {
                      if (canCreateSectorWebsite('YACHT_RENTAL')) {
                        setIndustry('YACHT_RENTAL');
                        setSelectedVibe('AEGEAN_COASTAL');
                        setErrorMsg(null);
                      }
                    }}
                    disabled={!canCreateSectorWebsite('YACHT_RENTAL')}
                    className={`p-4 rounded-xl border flex items-center justify-between transition ${
                      industry === 'YACHT_RENTAL'
                        ? 'bg-sky-950/60 border-sky-500 text-white shadow-lg shadow-sky-900/30 ring-1 ring-sky-500/50'
                        : 'bg-slate-800/50 border-slate-700 text-slate-400 hover:bg-slate-800'
                    } ${!canCreateSectorWebsite('YACHT_RENTAL') ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          industry === 'YACHT_RENTAL' ? 'bg-sky-500 text-white' : 'bg-slate-700 text-slate-300'
                        }`}
                      >
                        <Ship className="w-5 h-5" />
                      </div>
                      <div className="text-left">
                        <div className="font-bold text-sm">Yat & Tekne Kiralama</div>
                        <div className="text-[11px] text-slate-400">Mavi Tur, Gulet, Katamaran & Charter</div>
                      </div>
                    </div>
                    <div className="text-right pl-2">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${
                        sectorQuotas.YACHT_RENTAL.remaining > 0
                          ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30'
                          : 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                      }`}>
                        {sectorQuotas.YACHT_RENTAL.allowed
                          ? `${sectorQuotas.YACHT_RENTAL.remaining} Hak`
                          : 'Paketsiz'}
                      </span>
                    </div>
                  </button>

                  {/* Kurumsal Sektör */}
                  <button
                    type="button"
                    onClick={() => {
                      if (canCreateSectorWebsite('CORPORATE')) {
                        setIndustry('CORPORATE');
                        setSelectedVibe('CORPORATE_PRESTIGE');
                        setErrorMsg(null);
                      }
                    }}
                    disabled={!canCreateSectorWebsite('CORPORATE')}
                    className={`p-4 rounded-xl border flex items-center justify-between transition ${
                      industry === 'CORPORATE'
                        ? 'bg-indigo-950/60 border-indigo-500 text-white shadow-lg shadow-indigo-900/30 ring-1 ring-indigo-500/50'
                        : 'bg-slate-800/50 border-slate-700 text-slate-400 hover:bg-slate-800'
                    } ${!canCreateSectorWebsite('CORPORATE') ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          industry === 'CORPORATE' ? 'bg-indigo-500 text-white' : 'bg-slate-700 text-slate-300'
                        }`}
                      >
                        <Building2 className="w-5 h-5" />
                      </div>
                      <div className="text-left">
                        <div className="font-bold text-sm">Kurumsal & Şirket</div>
                        <div className="text-[11px] text-slate-400">Holding, Danışmanlık, Hizmet & B2B</div>
                      </div>
                    </div>
                    <div className="text-right pl-2">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${
                        sectorQuotas.CORPORATE.remaining > 0
                          ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                          : 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                      }`}>
                        {sectorQuotas.CORPORATE.allowed
                          ? `${sectorQuotas.CORPORATE.remaining} Hak`
                          : 'Paketsiz'}
                      </span>
                    </div>
                  </button>
                </div>
              </div>

              {/* Natural Language Prompt Box */}
              <div>
                <label className="text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1.5 block">
                  2. İşletmenizi Doğal Dille Anlatın (AI Prompt):
                </label>
                <div className="relative">
                  <textarea
                    rows={4}
                    value={promptText}
                    onChange={(e) => setPromptText(e.target.value)}
                    placeholder="Örn: Muğla Dalaman'da yat kiralama hizmeti veren premium bir firmayız. 8 adet lüks yatımız var..."
                    className="w-full px-4 py-3 bg-slate-950 border border-slate-700 rounded-xl text-white placeholder-slate-500 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition"
                  />
                  <div className="absolute right-3 bottom-3 flex items-center gap-1.5 text-[10px] text-slate-500">
                    <Sparkles className="w-3 h-3 text-indigo-400" />
                    Türkçe / English destekli
                  </div>
                </div>
              </div>

              {/* Business Details Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Şirket / Marka Adı *</label>
                  <input
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500"
                    placeholder="Örn: Azure Yachting"
                  />
                </div>

                <div>
                  <label className="text-xs text-slate-400 block mb-1">Şehir / Lokasyon</label>
                  <input
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500"
                    placeholder="Örn: Göcek / Muğla"
                  />
                </div>

                <div>
                  <label className="text-xs text-slate-400 block mb-1">Telefon / İletişim</label>
                  <input
                    type="text"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500"
                    placeholder="+90 (252) 000 00 00"
                  />
                </div>

                <div>
                  <label className="text-xs text-slate-400 block mb-1">WhatsApp İletişim Hattı</label>
                  <input
                    type="text"
                    value={whatsapp}
                    onChange={(e) => setWhatsapp(e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500"
                    placeholder="+90 (532) 000 00 00"
                  />
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: DESIGN VIBE */}
          {step === 'DESIGN_VIBE' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-semibold text-white mb-1">Tasarım Yönü ve Atmosfer</h3>
                <p className="text-xs text-slate-400">
                  Web sitenizin renk paleti, tipografi ritmi ve görsel hissini seçin.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {DESIGN_VIBES.map((vibe) => (
                  <button
                    key={vibe.id}
                    type="button"
                    onClick={() => setSelectedVibe(vibe.id)}
                    className={`p-4 rounded-xl border text-left transition relative overflow-hidden ${
                      selectedVibe === vibe.id
                        ? 'border-indigo-500 ring-2 ring-indigo-500/30 bg-slate-800'
                        : 'border-slate-800 bg-slate-950/70 hover:bg-slate-800/70 hover:border-slate-700'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-bold text-sm text-white">{vibe.title}</span>
                      {selectedVibe === vibe.id && (
                        <CheckCircle2 className="w-4 h-4 text-indigo-400" />
                      )}
                    </div>
                    <p className="text-xs text-slate-400 mb-3">{vibe.desc}</p>
                    <div className="flex items-center gap-1.5">
                      {vibe.colors.map((c, i) => (
                        <div
                          key={i}
                          className="w-6 h-6 rounded-full border border-white/20 shadow-sm"
                          style={{ backgroundColor: c }}
                        />
                      ))}
                    </div>
                  </button>
                ))}
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2 block">
                  Metin Dili & İletişim Tonu (Tone of Voice):
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    { id: 'PREMIUM_LUXURY', label: 'Lüks & Prestijli', desc: 'VIP, seçkin ve zarif' },
                    { id: 'PROFESSIONAL_CORPORATE', label: 'Kurumsal & Ciddi', desc: 'Güven veren, net ve oturaklı' },
                    { id: 'WARM_FRIENDLY', label: 'Sıcak & Samimi', desc: 'Misafirperver ve yakın' },
                    { id: 'DYNAMIC_INNOVATIVE', label: 'Dinamik & İnovatif', desc: 'Modern ve enerjik' },
                  ].map((tone) => (
                    <button
                      key={tone.id}
                      type="button"
                      onClick={() => setToneOfVoice(tone.id as any)}
                      className={`p-3 rounded-lg border text-left text-xs transition ${
                        toneOfVoice === tone.id
                          ? 'border-indigo-500 bg-indigo-950/40 text-white'
                          : 'border-slate-800 bg-slate-950 text-slate-400 hover:bg-slate-800'
                      }`}
                    >
                      <div className="font-bold mb-0.5">{tone.label}</div>
                      <div className="text-[10px] text-slate-500">{tone.desc}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: PAGES & MODULES */}
          {step === 'PAGES_MODULES' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-semibold text-white mb-1">
                  Oluşturulacak Sayfalar ve Bölüm Ağacı
                </h3>
                <p className="text-xs text-slate-400">
                  AI motorunun üreteceği sayfaları özelleştirin.
                </p>
              </div>

              <div className="space-y-2">
                {[
                  {
                    id: 'home',
                    title: 'Ana Sayfa (Landing Page)',
                    desc: 'Hero karşılama, sayılarla istatistikler, hakkımızda özeti, öne çıkanlar, yorumlar ve iletişim.',
                    required: true,
                  },
                  {
                    id: 'about',
                    title: 'Hakkımızda / Kurumsal',
                    desc: 'Şirket tarihi, misyon, vizyon, değerler ve yönetim ekibi.',
                    required: false,
                  },
                  {
                    id: 'fleet_or_services',
                    title: industry === 'YACHT_RENTAL' ? 'Yat Filosu & Kiralama' : 'Hizmetlerimiz Kataloğu',
                    desc:
                      industry === 'YACHT_RENTAL'
                        ? 'Tüm yatların teknik detayları, kabin, misafir kapasiteleri ve günlük/haftalık fiyatları.'
                        : 'Kapsamlı kurumsal hizmet paketleri ve çözüm detayları.',
                    required: false,
                  },
                  {
                    id: 'contact',
                    title: 'İletişim & Rezervasyon',
                    desc: 'Lokasyon haritası, iletişim formu, WhatsApp hızlı bağlantısı ve çalışma saatleri.',
                    required: false,
                  },
                  {
                    id: 'blog',
                    title: 'Haberler & Mavi Tur Rehberi',
                    desc: 'Sektörel makaleler, rota önerileri ve güncel kurumsal duyurular.',
                    required: false,
                  },
                ].map((pg) => {
                  const isChecked = selectedPages.includes(pg.id) || pg.required;
                  return (
                    <div
                      key={pg.id}
                      onClick={() => {
                        if (pg.required) return;
                        if (isChecked) {
                          setSelectedPages(selectedPages.filter((p) => p !== pg.id));
                        } else {
                          setSelectedPages([...selectedPages, pg.id]);
                        }
                      }}
                      className={`p-3.5 rounded-xl border flex items-center justify-between cursor-pointer transition ${
                        isChecked
                          ? 'border-indigo-500/60 bg-indigo-950/20'
                          : 'border-slate-800 bg-slate-950/60 opacity-60 hover:opacity-100'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-5 h-5 rounded flex items-center justify-center text-xs font-bold ${
                            isChecked ? 'bg-indigo-600 text-white' : 'border border-slate-700 bg-slate-900'
                          }`}
                        >
                          {isChecked && <Check className="w-3.5 h-3.5" />}
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-white flex items-center gap-2">
                            {pg.title}
                            {pg.required && (
                              <span className="text-[10px] px-1.5 py-0.2 bg-slate-800 text-slate-400 rounded">
                                Zorunlu
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-slate-400">{pg.desc}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Factual Integrity Banner */}
              <div className="p-4 rounded-xl bg-slate-950 border border-slate-800 flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-indigo-500/20 flex items-center justify-center text-indigo-400 flex-shrink-0">
                  <ShieldAlert className="w-4 h-4" />
                </div>
                <div className="text-xs text-slate-300">
                  <span className="font-semibold text-white block mb-0.5">
                    Güvenli ve Factual İçerik Politikası
                  </span>
                  AI motoru sahte sertifika veya uydurma ödüller üretmez. Yalnızca sağladığınız
                  gerçek işletme bilgileri ve Adiosoft onaylı güvenli bileşen şemaları kullanılır.
                </div>
              </div>
            </div>
          )}

          {/* STEP: GENERATING PROGRESS */}
          {step === 'GENERATING' && (
            <div className="py-12 px-4 text-center space-y-6">
              <div className="relative w-24 h-24 mx-auto">
                <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-indigo-500 to-sky-500 blur-xl opacity-40 animate-pulse" />
                <div className="relative w-full h-full rounded-full border-4 border-indigo-500/20 border-t-indigo-500 animate-spin flex items-center justify-center">
                  <Sparkles className="w-8 h-8 text-indigo-400 animate-bounce" />
                </div>
              </div>

              <div>
                <h3 className="text-lg font-bold text-white mb-1">
                  Yapay Zeka Web Sitenizi İnşa Ediyor...
                </h3>
                <p className="text-sm text-indigo-300 font-medium">
                  {jobProgress.message}
                </p>
                {jobProgress.details && (
                  <p className="text-xs text-slate-400 mt-1 max-w-md mx-auto">
                    {jobProgress.details}
                  </p>
                )}
              </div>

              {/* Progress bar */}
              <div className="max-w-md mx-auto space-y-2">
                <div className="w-full bg-slate-800 rounded-full h-2.5 overflow-hidden border border-slate-700">
                  <div
                    className="bg-gradient-to-r from-indigo-500 via-sky-500 to-emerald-400 h-full rounded-full transition-all duration-500"
                    style={{ width: `${jobProgress.progressPercent}%` }}
                  />
                </div>
                <div className="flex justify-between text-[11px] text-slate-400 font-mono">
                  <span>{jobProgress.stage}</span>
                  <span>%{jobProgress.progressPercent}</span>
                </div>
              </div>

              {/* Progress milestones */}
              <div className="max-w-md mx-auto grid grid-cols-2 gap-2 text-left text-xs">
                <div
                  className={`p-2 rounded-lg border ${
                    jobProgress.progressPercent >= 25
                      ? 'border-indigo-500/50 bg-indigo-950/30 text-indigo-200'
                      : 'border-slate-800 text-slate-500'
                  }`}
                >
                  ✓ Sektör Analizi & Vibe
                </div>
                <div
                  className={`p-2 rounded-lg border ${
                    jobProgress.progressPercent >= 50
                      ? 'border-indigo-500/50 bg-indigo-950/30 text-indigo-200'
                      : 'border-slate-800 text-slate-500'
                  }`}
                >
                  ✓ Sayfa Ağacı & Menü
                </div>
                <div
                  className={`p-2 rounded-lg border ${
                    jobProgress.progressPercent >= 75
                      ? 'border-indigo-500/50 bg-indigo-950/30 text-indigo-200'
                      : 'border-slate-800 text-slate-500'
                  }`}
                >
                  ✓ Bölümler & İçerik Yazımı
                </div>
                <div
                  className={`p-2 rounded-lg border ${
                    jobProgress.progressPercent >= 90
                      ? 'border-indigo-500/50 bg-indigo-950/30 text-indigo-200'
                      : 'border-slate-800 text-slate-500'
                  }`}
                >
                  ✓ Google SEO Meta Tagleri
                </div>
              </div>
            </div>
          )}

          {/* STEP: REVIEW DRAFT */}
          {step === 'REVIEW_DRAFT' && generatedPlan && (
            <div className="space-y-6">
              <div className="p-4 rounded-xl bg-emerald-950/40 border border-emerald-500/40 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-6 h-6 text-emerald-400 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm font-bold text-white">
                      Yapay Zeka Site Taslağınız Hazır!
                    </h3>
                    <p className="text-xs text-emerald-300/80">
                      Tüm sayfalar, bölümler ve SEO ayarları başarıyla derlendi.
                    </p>
                  </div>
                </div>
                <span className="px-2.5 py-1 text-xs font-bold rounded-lg bg-amber-500/20 text-amber-300 border border-amber-500/30">
                  DRAFT (Taslak)
                </span>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="p-3.5 rounded-xl bg-slate-950 border border-slate-800">
                  <span className="text-[11px] text-slate-400 block mb-1">Site & Marka Adı</span>
                  <div className="text-sm font-bold text-white">{generatedPlan.name}</div>
                  <div className="text-xs text-indigo-400 mt-0.5">
                    {generatedPlan.industry === 'YACHT_RENTAL' ? 'Yat Kiralama & Mavi Tur' : 'Kurumsal Holding'}
                  </div>
                </div>

                <div className="p-3.5 rounded-xl bg-slate-950 border border-slate-800">
                  <span className="text-[11px] text-slate-400 block mb-1">Renk & Tasarım Teması</span>
                  <div className="flex items-center gap-2 mt-1">
                    <div
                      className="w-4 h-4 rounded-full border border-white/20"
                      style={{ backgroundColor: generatedPlan.colorPalette.primaryColor }}
                    />
                    <div
                      className="w-4 h-4 rounded-full border border-white/20"
                      style={{ backgroundColor: generatedPlan.colorPalette.secondaryColor }}
                    />
                    <div
                      className="w-4 h-4 rounded-full border border-white/20"
                      style={{ backgroundColor: generatedPlan.colorPalette.accentColor }}
                    />
                    <span className="text-xs font-mono text-slate-300 ml-1">
                      {generatedPlan.vibe}
                    </span>
                  </div>
                </div>

                <div className="p-3.5 rounded-xl bg-slate-950 border border-slate-800">
                  <span className="text-[11px] text-slate-400 block mb-1">Üretilen Sayfa Sayısı</span>
                  <div className="text-sm font-bold text-white">
                    {generatedPlan.pages.length} Sayfa /{' '}
                    {generatedPlan.pages.reduce((acc, p) => acc + (p.sections?.length || 0), 0)} Bölüm
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5">
                    {generatedPlan.yachts?.length ? `${generatedPlan.yachts.length} Adet Yat Filosu` : 'Tam Donanımlı'}
                  </div>
                </div>
              </div>

              {/* Pages & Sections Preview List */}
              <div>
                <label className="text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2 block">
                  Sayfa & Bölüm Hiyerarşisi:
                </label>
                <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                  {generatedPlan.pages.map((p, pIdx) => (
                    <div
                      key={pIdx}
                      className="p-3 rounded-lg bg-slate-950 border border-slate-800 flex items-center justify-between text-xs"
                    >
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-indigo-400" />
                        <span className="font-semibold text-white">{p.title}</span>
                        <span className="text-slate-500 font-mono">/{p.slug}</span>
                      </div>
                      <div className="text-slate-400">
                        {p.sections?.length || 0} Bileşen (
                        {p.sections?.map((s) => s.type).slice(0, 3).join(', ')}...)
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* SEO Summary */}
              <div className="p-3.5 rounded-xl bg-slate-950 border border-slate-800">
                <span className="text-[11px] font-semibold text-slate-300 block mb-1 flex items-center gap-1.5">
                  <Globe className="w-3.5 h-3.5 text-sky-400" /> Google SEO Başlığı & Açıklaması
                </span>
                <div className="text-xs font-bold text-sky-300">{generatedPlan.seo.metaTitle}</div>
                <div className="text-xs text-slate-400 mt-0.5">{generatedPlan.seo.metaDescription}</div>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer Controls */}
        <div className="px-6 py-4 border-t border-slate-800 bg-slate-950/80 flex items-center justify-between">
          {step === 'PROMPT_INPUT' && (
            <>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm text-slate-400 hover:text-white transition"
              >
                İptal
              </button>
              <button
                type="button"
                onClick={() => setStep('DESIGN_VIBE')}
                disabled={!companyName.trim() || !canCreateSectorWebsite(industry)}
                className="px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold flex items-center gap-2 transition disabled:opacity-50 shadow-lg shadow-indigo-600/30"
              >
                İleri: Tasarım Yönü
                <ArrowRight className="w-4 h-4" />
              </button>
            </>
          )}

          {step === 'DESIGN_VIBE' && (
            <>
              <button
                type="button"
                onClick={() => setStep('PROMPT_INPUT')}
                className="px-4 py-2 text-sm text-slate-400 hover:text-white flex items-center gap-1.5 transition"
              >
                <ArrowLeft className="w-4 h-4" /> Geri
              </button>
              <button
                type="button"
                onClick={() => setStep('PAGES_MODULES')}
                className="px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold flex items-center gap-2 transition shadow-lg shadow-indigo-600/30"
              >
                İleri: Sayfalar & Modüller
                <ArrowRight className="w-4 h-4" />
              </button>
            </>
          )}

          {step === 'PAGES_MODULES' && (
            <>
              <button
                type="button"
                onClick={() => setStep('DESIGN_VIBE')}
                className="px-4 py-2 text-sm text-slate-400 hover:text-white flex items-center gap-1.5 transition"
              >
                <ArrowLeft className="w-4 h-4" /> Geri
              </button>
              <button
                type="button"
                onClick={handleStartGeneration}
                className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-sky-600 hover:from-indigo-500 hover:to-sky-500 text-white text-sm font-bold flex items-center gap-2 transition shadow-xl shadow-indigo-500/25"
              >
                <Sparkles className="w-4 h-4" />
                AI ile Siteyi Oluştur
              </button>
            </>
          )}

          {step === 'GENERATING' && (
            <div className="w-full text-center py-1 text-xs text-slate-500">
              Lütfen bekleyin, site planı yapılandırılıyor...
            </div>
          )}

          {step === 'REVIEW_DRAFT' && (
            <>
              <button
                type="button"
                onClick={() => setStep('PROMPT_INPUT')}
                className="px-4 py-2 text-sm text-slate-400 hover:text-white flex items-center gap-1.5 transition"
              >
                <RefreshCw className="w-4 h-4" /> Yeniden Düzenle
              </button>
              <button
                type="button"
                onClick={handleAcceptAndOpenEditor}
                disabled={isSaving}
                className="px-6 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold flex items-center gap-2 transition shadow-lg shadow-emerald-600/30 disabled:opacity-50"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Editöre Aktarılıyor...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Taslağı Onayla & Editörde Aç
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AISiteGeneratorWizard;
